import type { QualificationTemplate } from '../../lib/api';
import { isAuditTemplate } from '../../lib/questionnaireLabels';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { ClipboardCheck, FileText } from 'lucide-react';
import { TYPE_CONFIG } from './templateTypeConfig';

interface TemplateNameCellProps {
  template: Pick<QualificationTemplate, 'name' | 'description' | 'template_type' | 'respondent'>;
}

/**
 * Name column of the Templates list: type icon, name, an "Audit" badge for
 * Audit Lite templates (completed by the customer's own auditor), description.
 */
export default function TemplateNameCell({ template }: TemplateNameCellProps) {
  const config = TYPE_CONFIG[template.template_type];
  return (
    <div className="flex items-center gap-3">
      <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-lg ${
        config?.bg ?? 'bg-gray-50'
      } ${config?.text ?? 'text-gray-600'}`}>
        {config?.icon ?? <FileText className="h-5 w-5" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
            {template.name}
          </span>
          {isAuditTemplate(template) && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline"
                    className="gap-1 border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                    <ClipboardCheck className="h-3 w-3" />Audit
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Completed by your own auditor under Questionnaires › Audits. Never sent to suppliers.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {template.description && (
          <div className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
            {template.description}
          </div>
        )}
      </div>
    </div>
  );
}
